import { useEffect, useRef, useState } from "react";

function useSearchBar(data: any) {

    const [searchEntry, setSearchEntry] = useState("");
    const [isSearchFocused, setIsSearchFocused] = useState(false);
    const searchRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
          if (
            searchRef.current &&
            !searchRef.current.contains(event.target as Node)
          ) {
            setIsSearchFocused(false);
          }
        };
    
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
          document.removeEventListener("mousedown", handleClickOutside);
        };
      }, []);

    const normalizeMonthSearch = (search: string): string => {
    const monthMap: { [key: string]: string } = {
      januari: "01",
      februari: "02",
      maart: "03",
      april: "04",
      mei: "05",
      juni: "06",
      juli: "07",
      augustus: "08",
      september: "09",
      oktober: "10",
      november: "11",
      december: "12",
    };

    const patterns = [
      // Full month name with year: "march 2025" -> "2503"
      /(januari|februari|maart|mei|juni|juli|augustus|september|oktober|november|december)\s+(\d{4})/i,

      // Month number with year: "03 2025" -> "2503"
      /(\d{1,2})\s+(\d{4})/,

      // Year with month name: "2025 march" -> "2503"
      /(\d{4})\s+(januari|februari|maart|mei|juni|juli|augustus|september|oktober|november|december)/i,

      // Year with month number: "2025 03" -> "2503"
      /(\d{4})\s+(\d{1,2})/,
    ];

    for (const pattern of patterns) {
      const match = search.match(pattern);
      if (match) {
        let month, year;

        if (pattern.source.includes("\\d{4}\\s+")) {
          // Pattern starts with year
          year = match[1];
          month = match[2];
        } else {
          // Pattern starts with month
          month = match[1];
          year = match[2];
        }

        // Convert month name to number if needed
        const monthNum =
          monthMap[month.toLowerCase()] ||
          (month.length === 1
            ? `0${month}`
            : month.length === 2
              ? month
              : `0${month}`.slice(-2));

        // Format as "YYMM" (last 2 digits of year + month)
        const yearShort = year.slice(-2);
        return `${yearShort}${monthNum}`;
      }
    }

    return search;
  };

  const shouldShowFilters = isSearchFocused && searchEntry.trim() === "";

  const handleFilterClick = (filterText: string) => {
    setSearchEntry(filterText);
    setIsSearchFocused(false);
  };

const getFilteredSearchProjects = () => {
    let filteredProjects = [...data];

    // case-insensitive sorting stuff
    if (searchEntry.trim() !== "") {
      const search = searchEntry.toLowerCase().trim();

      if (search.startsWith("van:")) {
        const customerSearch = search.substring(4).trim();

        if (customerSearch) {
          filteredProjects = filteredProjects.filter((p) =>
            p.customer?.toLowerCase().includes(customerSearch),
          );
        }
      } else if (search.startsWith("meth:")) {
        const methodSearch = search.substring(5).trim();

        if (methodSearch) {
          filteredProjects = filteredProjects.filter((p) =>
            p.methode?.toLowerCase().includes(methodSearch),
          );
        }
      } else if (search.startsWith("per:")) {
        const refrenceSearch = search.substring(4).trim();

        if (refrenceSearch) {
          // Try to parse month names to numbers
          const normalizedSearch = normalizeMonthSearch(refrenceSearch);

          filteredProjects = filteredProjects.filter((p) => {
            if (!p.refrence) return false;

            const refLower = p.refrence.toLowerCase();
            // Search for both original and normalized version
            return (
              refLower.includes(refrenceSearch) ||
              refLower.includes(normalizedSearch)
            );
          });
        }
      } else {
        filteredProjects = filteredProjects.filter(
          (p) =>
            p.project?.toLowerCase().includes(search) ||
            p.customer?.toLowerCase().includes(search) ||
            p.refrence?.toLowerCase().includes(search) ||
            p.methode?.toLowerCase().includes(search) ||
            p.number?.toLowerCase().includes(search) ||
            p.description?.toLowerCase().includes(search) ||
            p.client?.toLowerCase().includes(search) ||
            p.status?.toLowerCase().includes(search) ||
            p.requester?.toLowerCase().includes(search) ||
            p.coordinator?.toLowerCase().includes(search) ||
            p.type?.toLowerCase().includes(search) ||
            p.time?.toLowerCase().includes(search) ||
            p.target?.toLowerCase().includes(search) ||
            p.priority?.toLowerCase().includes(search) ||
            p.call_type?.toLowerCase().includes(search) ||
            p.line?.toLowerCase().includes(search) ||
            p.caller?.toLowerCase().includes(search) ||
            p.operator?.toLowerCase().includes(search) ||
            p.operator_group?.toLowerCase().includes(search) ||
            p.period?.toLowerCase().includes(search) ||
            p.name?.toLowerCase().includes(search)
        );
      }
    }
    return filteredProjects;
  };

  // Return all the functions and states
  return {
    // Sort states
    searchEntry,
    isSearchFocused,
    setSearchEntry,
    setIsSearchFocused,
    handleFilterClick,
    shouldShowFilters,
    getFilteredSearchProjects,
    searchRef,
  };
}

export { useSearchBar };
